/**
 * ESCHER FALLS — 設定画面と、盤面の隅に置く唯一の入口。
 *
 * `#ui` の中に自前の DOM と `<style>` を持つ自己完結モジュール。
 * `createHud` が `#ui` を `replaceChildren` するので、**必ずその後に生成すること**。
 *
 * ## 隅の入口の置き場
 * **左上。** 右上はステージ選択の入口（「ステージ」）が持っているので譲る。
 * 一度この設定の中へ畳んでみたが、遊んでいる最中にステージを選び直す道が
 * 二段深くなって使いづらかったので、入口は2つに分けた。
 * 左上はステージ名やタイムの居場所なので、そのぶん文字を右へ寄せている。
 *
 * ## 「音を試す」がある理由
 * 音が鳴らないとき、原因は3つに分かれる——**解錠されていない／端末が消音／
 * ただ音量が小さい**。どれなのかは中の人しか分からないので、
 * 状態を1行で見せ、その場で鳴らせるようにした。
 * 押すこと自体が利用者の操作なので、**解錠の最後の砦にもなっている。**
 *
 * ## 音量つまみが2本しかないこと
 * この作品で操れて意味があるのは「場の音」と「操作の手応え」の2つだけである。
 */

import { DEFAULT_AUDIO, type AudioSettings } from '../game/settings';
import { SKINS, type Skin, type SkinState } from '../game/skins';
import { SHOP_PRODUCTS, displayPrice, productName } from '../game/iap';
import { t, pickedLang, setLang, LANG_NAMES, type Lang } from './i18n';
import type { AudioStateKey } from '../audio/engine';

const STYLE_ID = 'ef-settings-style';

const CSS = `
.ef-set p, .ef-set label { margin: 0; }

/* 設定の入口は**左上**。右上はステージ選択の入口が持っているので譲る。 */
.ef-set-open {
  position: absolute;
  top: calc(var(--hud-gap, 16px) + var(--safe-top, 0px));
  left: calc(var(--hud-gap, 16px) + var(--safe-left, 0px));
  font-size: 15px;
  line-height: 1;
  padding: 8px 12px;
  letter-spacing: 0;
  z-index: 10;
}

/* 左上はステージ名・ヒント・水量・タイムの居場所なので、そのぶん右へ寄せる。
   この押しのけは設定ボタンが作った不都合なので、設定側で面倒を見る
   （hud.ts は設定の存在を知らないままでよい）。 */
#ui .hud-topleft { padding-left: 50px; }

.ef-set {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 26px;
  padding: calc(var(--safe-top, 0px) + 32px) 24px calc(var(--safe-bottom, 0px) + 32px);
  background: var(--paper, #f2efe6);
  pointer-events: auto;
}
.ef-set.is-on { display: flex; }

.ef-set-title {
  font-family: var(--font-serif, serif);
  font-size: 26px;
  letter-spacing: 0.4em;
  text-indent: 0.4em;
  color: var(--ink, #2b2823);
}

.ef-set-rows { display: flex; flex-direction: column; gap: 22px; width: 100%; max-width: 380px; }
.ef-set-row { display: flex; flex-direction: column; gap: 9px; }
.ef-set-row-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--font-serif, serif);
  font-size: 15px;
  letter-spacing: 0.24em;
  color: var(--ink, #2b2823);
}
.ef-set-val {
  font-size: 13px;
  letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
  color: var(--ink-soft, #6b665c);
}

/* つまみ。ブラウザ既定の見た目は紙と線の意匠に合わないので自分で描く。 */
.ef-set-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 26px;
  background: transparent;
  cursor: pointer;
}
.ef-set-range::-webkit-slider-runnable-track {
  height: 1px;
  background: var(--ink-soft, #6b665c);
}
.ef-set-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  margin-top: -11px;
  border: 1px solid var(--ink, #2b2823);
  border-radius: 50%;
  background: var(--water, #4a8c8a);
}
.ef-set-range::-moz-range-track { height: 1px; background: var(--ink-soft, #6b665c); }
.ef-set-range::-moz-range-thumb {
  width: 20px; height: 20px; border: 1px solid var(--ink, #2b2823);
  border-radius: 50%; background: var(--water, #4a8c8a);
}

.ef-set-note {
  font-size: 12px;
  letter-spacing: 0.1em;
  line-height: 1.8;
  color: var(--ink-soft, #6b665c);
  text-align: center;
  max-width: 380px;
  text-wrap: balance;
}

/* 言語は数が多いので2列に積む。**その言語自身の綴りで出す**ので、
   字幅がまるで違う（Bahasa Indonesia と ไทย）。中央寄せにせず左に揃える。 */
.ef-lang-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.ef-lang-btn { font-size: 12px; letter-spacing: 0.04em; padding: 8px 10px; text-align: center; }
.ef-lang-btn.is-on { background: var(--ink); color: var(--paper); }

.ef-set-links { display: flex; flex-direction: column; gap: 8px; }
.ef-set-links[hidden] { display: none; }
.ef-set-link { width: 100%; font-size: 13px; }
.ef-set-actions { display: flex; flex-direction: column; gap: 12px; align-items: center; }

/* 画面が縦に伸びるので、中央寄せをやめて上から積む。
   着せ替えと売り場が入って、1画面に収まらなくなった。 */
.ef-set { justify-content: flex-start; overflow-y: auto; gap: 22px; }

.ef-set-section { width: 100%; max-width: 380px; }
.ef-set-section-title {
  font-size: 12px;
  letter-spacing: 0.3em;
  color: var(--ink-soft, #6b665c);
  border-bottom: 1px solid var(--ink-soft, #6b665c);
  padding-bottom: 5px;
  margin-bottom: 12px;
}

/* 配色の見本。**色そのものが商品**なので、名前より見本を大きく見せる。 */
.ef-skin-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); gap: 10px; }
.ef-skin {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 9px 6px 8px;
  border: 1px solid var(--ink-soft, #6b665c);
  background: transparent;
  color: var(--ink, #2b2823);
  font-family: var(--font-serif, serif);
  cursor: pointer;
}
.ef-skin.is-selected { border-color: var(--water, #4a8c8a); border-width: 2px; padding: 8px 5px 7px; }
.ef-skin:disabled { cursor: default; opacity: 0.45; }
/* 見本は「紙の上に石と水」。盤面の縮図になっている。 */
.ef-skin-swatch {
  width: 100%; height: 34px;
  display: flex; align-items: stretch;
  border: 1px solid var(--ink-soft, #6b665c);
}
.ef-skin-swatch i { flex: 1; }
.ef-skin-name { font-size: 12px; letter-spacing: 0.12em; }
.ef-skin-cost {
  font-size: 10px; letter-spacing: 0.06em;
  font-variant-numeric: tabular-nums;
  color: var(--water, #4a8c8a);
}
.ef-skin-cost.is-owned { color: var(--ink-soft, #6b665c); }

/* 売り場。**提供者が居なければ節ごと出さない。** */
.ef-shop-row {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 9px 0;
  border-bottom: 1px solid var(--paper-shade, #e6e2d6);
}
.ef-shop-name { font-family: var(--font-serif, serif); font-size: 14px; letter-spacing: 0.1em; }
.ef-shop-buy { font-size: 12px; padding: 6px 12px; white-space: nowrap; }
.ef-shop-restore { margin-top: 14px; text-align: center; }
.ef-shop-restore-btn { font-size: 12px; padding: 7px 18px; }
.ef-shop-restore-note {
  margin-top: 8px;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: var(--ink-soft, #6b665c);
  min-height: 1em;
}
.ef-set-section[hidden] { display: none; }
.ef-set-diag { display: flex; flex-direction: column; gap: 10px; align-items: center; }
`;

let styleRefCount = 0;

function injectStyles(): void {
  styleRefCount += 1;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function releaseStyles(): void {
  styleRefCount = Math.max(0, styleRefCount - 1);
  if (styleRefCount > 0) return;
  document.getElementById(STYLE_ID)?.remove();
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export interface SettingsCallbacks {
  /** いまの音量。開くたびに呼ぶ。 */
  getAudio(): AudioSettings;
  /**
   * つまみを動かした。**動かしている最中にも呼ばれる**ので、
   * ここで即座に音へ反映すること（耳で合わせられないと調整できない）。
   */
  onAudioChange(next: AudioSettings): void;
  /** つまみから指が離れた。ここで保存する（動かす途中で毎回書き込まない）。 */
  onAudioCommit(next: AudioSettings): void;
  /** 「ステージを選ぶ」。 */
  onOpenStages(): void;
  /** 「音を試す」。押されたら試聴音を鳴らす。 */
  onTestSound(): void;
  /** 音の状態を1行で。開くたびと試聴のたびに呼ぶ。 */
  getAudioStatus(): AudioStateKey;
  /**
   * App Store を開く。**省略可**で、省略すると節ごと出ない
   * （ブラウザでは開けないので、押せないボタンを並べない）。
   */
  onOpenStoreLink?(which: 'review' | 'developer'): void;
  /** 手持ちの雫と、持っている配色。開くたびに呼ぶ。 */
  getSkinState(): { skins: SkinState; drops: number };
  /** 配色を選ぶ／買う。買えなければ false を返す。 */
  onPickSkin(id: string): boolean;
  /**
   * 商品を買う。**省略すると売り場ごと出さない。**
   * 課金の提供者が居ないときに押せないボタンを出さないための口。
   */
  onPurchase?(productId: string): Promise<boolean>;
  /**
   * いま売り場を出してよいか。**開くたびに呼ぶ。**
   *
   * 生成時に一度だけ決めてはいけない。課金の登録は非同期で後から済むので、
   * 生成時点では必ず「まだ」になる（実際にそれで売り場が永久に出なかった）。
   * 広告の `canExtendTime` と同じ理由。
   */
  canPurchase?(): boolean;
  /**
   * 「購入を復元」。**非消耗品を買い直させないための義務**（ガイドライン 3.1.1）。
   *
   * ## 起動時の自動復元では足りない
   * 起動のたびに `restore` を呼んではいたが、**Apple はそれでは認めない。**
   * 「はっきりしたボタンを置き、押されたときに復元を始めること」を要求する。
   * 実際にこれで審査に落ちた（2026-08-24、3.1.1）。
   *
   * 戻り値は復元した商品の数。0 でも失敗ではない（買っていないだけ）。
   */
  onRestore?(): Promise<number>;
  /** 開いた／閉じた。main が入力の抑止に使う。 */
  onVisibilityChange?(open: boolean): void;
}

export interface Settings {
  readonly isOpen: boolean;
  /** 中身を組み直す。雫が増減したときに外から呼ぶ。 */
  refresh(): void;
  open(): void;
  close(): void;
  toggle(): void;
  /** 盤面の隅の入口を出す／隠す。ステージ選択や祝いの画面が出ている間は隠す。 */
  setOpenButtonVisible(visible: boolean): void;
  dispose(): void;
}

export function createSettings(root: HTMLElement, callbacks: SettingsCallbacks): Settings {
  injectStyles();

  const openBtn = el('button', 'btn ef-set-open', '≡');
  openBtn.type = 'button';
  openBtn.setAttribute('aria-label', t.menuAria);

  const bound: Array<{ el: EventTarget; type: string; fn: EventListener }> = [];
  const on = (target: EventTarget, type: string, fn: EventListener): void => {
    target.addEventListener(type, fn);
    bound.push({ el: target, type, fn });
  };

  const panel = el('div', 'ef-set');
  const title = el('p', 'ef-set-title', t.settings);

  const rows = el('div', 'ef-set-rows');

  /** 1本ぶんのつまみを作る。 */
  function makeRow(label: string): {
    row: HTMLDivElement;
    range: HTMLInputElement;
    val: HTMLSpanElement;
  } {
    const row = el('div', 'ef-set-row');
    const head = el('div', 'ef-set-row-head');
    const name = document.createElement('span');
    name.textContent = label;
    const val = el('span', 'ef-set-val');
    head.append(name, val);
    const range = el('input', 'ef-set-range');
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    range.step = '1';
    range.setAttribute('aria-label', label);
    row.append(head, range);
    return { row, range, val };
  }

  const bgmRow = makeRow(t.bgm);
  const seRow = makeRow(t.se);
  rows.append(bgmRow.row, seRow.row);

  // 「音が鳴らない」と言われたときに、どこまで動いているかを利用者自身が見られる。
  // 状態が「動作中」で、試聴しても聞こえないなら、原因は端末の音量か出力先である。
  const status = el('p', 'ef-set-note');
  const testBtn = el('button', 'btn ef-set-test', t.testSound);
  testBtn.type = 'button';
  const note = el('div', 'ef-set-diag');
  note.append(status, testBtn);

  // ── 配色（着せ替え）──────────────────────────────
  const skinSection = el('div', 'ef-set-section');
  const skinTitle = el('p', 'ef-set-section-title', t.palette);
  const skinGrid = el('div', 'ef-skin-grid');
  skinSection.append(skinTitle, skinGrid);

  /** 見本を1つ作る。持っていなければ値段、持っていれば「所持」。 */
  function makeSkinCard(skin: Skin, owned: boolean, selected: boolean, drops: number): HTMLButtonElement {
    const card = el('button', 'ef-skin');
    card.type = 'button';
    card.classList.toggle('is-selected', selected);
    // 買えないものは押せない。押して「足りません」と言うより、
    // 押せないことが見えているほうが親切である。
    card.disabled = !owned && drops < skin.cost;

    const sw = el('div', 'ef-skin-swatch');
    const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;
    for (const c of [skin.colors.paper, skin.colors.baseStoneDark, skin.colors.water, skin.colors.ink]) {
      const band = document.createElement('i');
      band.style.background = hex(c);
      sw.appendChild(band);
    }
    const cost = el('span', 'ef-skin-cost', owned ? (selected ? t.worn : t.owned) : t.drops(skin.cost));
    cost.classList.toggle('is-owned', owned);
    card.append(sw, el('span', 'ef-skin-name', t.skin[skin.id] ?? skin.name), cost);
    on(card, 'click', () => {
      if (callbacks.onPickSkin(skin.id)) renderSkins();
    });
    return card;
  }

  function renderSkins(): void {
    const { skins, drops } = callbacks.getSkinState();
    skinGrid.replaceChildren(
      ...SKINS.map((sk) =>
        makeSkinCard(sk, skins.owned.includes(sk.id), skins.selected === sk.id, drops),
      ),
    );
  }

  // ── 売り場（課金）────────────────────────────────
  const shopSection = el('div', 'ef-set-section');
  const shopTitle = el('p', 'ef-set-section-title', t.buyDrops);
  shopSection.append(shopTitle);
  // 出すかどうかは render のたびに決める（下の renderShop）。

  if (callbacks.onPurchase) {
    const buy = callbacks.onPurchase.bind(callbacks);
    for (const product of SHOP_PRODUCTS) {
      const row = el('div', 'ef-shop-row');
      const btn = el('button', 'btn ef-shop-buy', displayPrice(product));
      btn.type = 'button';
      on(btn, 'click', () => {
        btn.disabled = true;
        void buy(product.id).then((okay) => {
          btn.disabled = false;
          if (okay) render();
        });
      });
      row.append(el('span', 'ef-shop-name', productName(product)), btn);
      shopSection.append(row);
    }
  }

  // **復元は買い場と同じ節に置く。** 買った物を取り戻す場所は、
  // 買う場所の隣にあるのが自然で、探さずに見つかる。
  const restoreRow = el('div', 'ef-shop-restore');
  const restoreBtn = el('button', 'btn ef-shop-restore-btn', t.restore);
  restoreBtn.type = 'button';
  const restoreNote = el('p', 'ef-shop-restore-note');
  restoreRow.append(restoreBtn, restoreNote);
  if (callbacks.onRestore) {
    const restore = callbacks.onRestore.bind(callbacks);
    on(restoreBtn, 'click', () => {
      restoreBtn.disabled = true;
      restoreNote.textContent = t.restoreChecking;
      void restore()
        .then((count) => {
          // **0 件でも失敗ではない。** 買っていないだけなので、そう言う。
          restoreNote.textContent =
            count > 0 ? t.restoreDone(count) : t.restoreNone;
          render();
        })
        .catch(() => {
          restoreNote.textContent = t.restoreFailed;
        })
        .finally(() => {
          restoreBtn.disabled = false;
        });
    });
    shopSection.append(restoreRow);
  }

  // ## 言語
  //
  // **端末の設定だけに従うのでは足りない。** 台湾の人が英語の端末を使っていれば
  // 英語が出るし、そもそも12言語を実機で見て確かめる手立てが無くなる。
  // 選んだら読み込み直す（`setLang` の注記）。
  const langSection = el('div', 'ef-set-section ef-set-lang');
  const langTitle = el('p', 'ef-set-section-title', t.language);
  const langGrid = el('div', 'ef-lang-grid');
  const pickedNow = pickedLang();
  const entries: Array<[Lang | null, string]> = [
    [null, t.languageAuto],
    ...(Object.keys(LANG_NAMES) as Lang[]).map((k) => [k, LANG_NAMES[k]] as [Lang, string]),
  ];
  for (const [code, label] of entries) {
    const b = el('button', 'btn ef-lang-btn', label);
    b.type = 'button';
    // 「端末に合わせる」は、手で選んでいないときだけ選択中に見える。
    b.classList.toggle('is-on', code === null ? pickedNow === null : pickedNow === code);
    on(b, 'click', () => setLang(code));
    langGrid.append(b);
  }
  langSection.append(langTitle, langGrid);

  // ## App Store への出口
  //
  // **自分から書きに行きたい人の道を塞がない。** 自動の依頼（`game/review.ts`）は
  // OS が年3回までしか通さないので、書きたいのに出会えない人が必ず残る。
  //
  // 「他の作品」は作者の一覧ページへ送る。**アプリを1本ずつ並べない**——
  // 増えるたびにこちらを直すことになるし、まだ審査を通っていないものを
  // 載せると開いた先が404になる。
  const linkSection = el('div', 'ef-set-section ef-set-links');
  const reviewBtn = el('button', 'btn ef-set-link', t.writeReview);
  const moreBtn = el('button', 'btn ef-set-link', t.moreApps);
  reviewBtn.type = 'button';
  moreBtn.type = 'button';
  linkSection.append(reviewBtn, moreBtn);
  linkSection.hidden = callbacks.onOpenStoreLink === undefined;
  if (callbacks.onOpenStoreLink) {
    const openLink = callbacks.onOpenStoreLink.bind(callbacks);
    on(reviewBtn, 'click', () => openLink('review'));
    on(moreBtn, 'click', () => openLink('developer'));
  }

  const actions = el('div', 'ef-set-actions');
  const stagesBtn = el('button', 'btn ef-set-stages', t.chooseStage);
  const closeBtn = el('button', 'btn ef-set-close', t.resume);
  stagesBtn.type = 'button';
  closeBtn.type = 'button';
  actions.append(stagesBtn, closeBtn);

  panel.append(title, rows, note, skinSection, shopSection, langSection, linkSection, actions);
  panel.inert = true;
  root.append(openBtn, panel);

  let open = false;

  /** つまみの値から設定を組む。 */
  function current(): AudioSettings {
    return {
      bgm: Number(bgmRow.range.value) / 100,
      se: Number(seRow.range.value) / 100,
    };
  }

  function paint(): void {
    bgmRow.val.textContent = `${bgmRow.range.value}`;
    seRow.val.textContent = `${seRow.range.value}`;
  }

  /** 売り場の出し分け。**提供者が用意できたかを毎回訊く**（iap.ts 規則1）。 */
  function renderShop(): void {
    shopSection.hidden =
      callbacks.onPurchase === undefined || callbacks.canPurchase?.() === false;
    if (shopSection.hidden) return;
    // 価格はストアが返すので、開くたびに引き直す。
    for (const [i, product] of SHOP_PRODUCTS.entries()) {
      const btn = shopSection.querySelectorAll('.ef-shop-buy')[i];
      if (btn instanceof HTMLElement) btn.textContent = displayPrice(product);
    }
  }

  function render(): void {
    renderSkins();
    renderShop();
    status.textContent = t.audioStatus(t.audioState[callbacks.getAudioStatus()]);
    const a = callbacks.getAudio();
    bgmRow.range.value = String(Math.round((a.bgm ?? DEFAULT_AUDIO.bgm) * 100));
    seRow.range.value = String(Math.round((a.se ?? DEFAULT_AUDIO.se) * 100));
    paint();
  }

  for (const r of [bgmRow, seRow]) {
    // input は動かしている最中に飛ぶ。耳で合わせられるよう即反映する。
    on(r.range, 'input', () => {
      paint();
      callbacks.onAudioChange(current());
    });
    // change は指が離れたとき。保存はここだけ。
    on(r.range, 'change', () => callbacks.onAudioCommit(current()));
  }

  function setOpen(next: boolean): void {
    if (open === next) return;
    open = next;
    panel.classList.toggle('is-on', open);
    panel.inert = !open;
    openBtn.inert = open;
    openBtn.hidden = open;
    callbacks.onVisibilityChange?.(open);
    if (open) closeBtn.focus({ preventScroll: true });
  }

  on(openBtn, 'click', () => {
    render();
    setOpen(true);
  });
  on(testBtn, 'click', () => {
    callbacks.onTestSound();
    // 押した結果で状態が変わる（解錠が走る）ので、その場で表示を更新する。
    status.textContent = t.audioStatus(t.audioState[callbacks.getAudioStatus()]);
  });
  on(closeBtn, 'click', () => setOpen(false));
  on(stagesBtn, 'click', () => {
    setOpen(false);
    callbacks.onOpenStages();
  });
  on(window, 'keydown', (event) => {
    if (!open) return;
    if ((event as KeyboardEvent).key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
  });

  return {
    get isOpen(): boolean {
      return open;
    },
    refresh: render,
    open() {
      render();
      setOpen(true);
    },
    close() {
      setOpen(false);
    },
    toggle() {
      if (open) {
        setOpen(false);
        return;
      }
      render();
      setOpen(true);
    },
    setOpenButtonVisible(visible: boolean) {
      // 開いている間は setOpen 側が hidden を握っているので触らない。
      if (open) return;
      openBtn.hidden = !visible;
      openBtn.inert = !visible;
    },
    dispose() {
      for (const b of bound) b.el.removeEventListener(b.type, b.fn);
      bound.length = 0;
      openBtn.remove();
      panel.remove();
      releaseStyles();
    },
  };
}
